import React, { useState } from "react";
import { UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NotifType } from "../types";
import type { SenderProfile } from "../hooks/useNotifSenders";
import { getNotifTheme, type NotifTheme } from "../lib/icons";

interface NotifAvatarProps {
  type: NotifType;
  /**
   * - `SenderProfile`  → resolved successfully.
   * - `null`           → sender was requested but no longer exists
   *                      (account deleted → render "Former Member").
   * - `undefined`      → no sender expected (system alert) or not
   *                      yet resolved.
   */
  sender: SenderProfile | null | undefined;
  compact?: boolean;
}

/**
 * Leading visual for every notification row.
 *
 * Decision tree:
 *   0. `sender === null` (deleted user) → gray `UserX` icon chip with
 *      aria-label "Former member". Never crashes even if a broken
 *      `avatar_url` gets cached for an account that was purged.
 *   1. If we have a `sender.avatar_url` → render the photo on top of
 *      an initials/icon fallback layer so a 404 on the image still
 *      shows something sensible instead of an empty square.
 *   2. If we have a sender name/email but no photo → initials chip
 *      tinted with the notification type's theme colour.
 *   3. Otherwise (system alerts: attendance / ai_insight / system) →
 *      the contextual Lucide icon on a soft type-coloured background.
 */
export function NotifAvatar({ type, sender, compact }: NotifAvatarProps) {
  // Defensive lookup: a DB row can arrive with a `type` value that's not
  // in our compile-time union (new types added server-side before a
  // client deploy, cached rows from older schemas, etc.). Falling back
  // to a neutral theme keeps the bell from crashing the whole header.
  const theme = getNotifTheme(type);
  const size  = compact ? "h-7 w-7" : "h-8 w-8";

  // 0. Deleted / orphaned user (Sprint 1.5 crash-proofing).
  if (sender === null) {
    return (
      <div
        className={cn(
          "rounded-full flex items-center justify-center flex-shrink-0",
          "bg-muted text-muted-foreground ring-1 ring-border/60",
          size,
        )}
        aria-label="Former member"
        title="Former member"
      >
        <UserX className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }

  // 1. Photo avatar — with a resilient fallback layer underneath.
  if (sender?.avatar_url) {
    return <PhotoAvatar sender={sender} size={size} theme={theme} />;
  }

  // 2. Initials chip
  const initials = senderInitials(sender);
  if (initials) {
    return (
      <div
        className={cn(
          "rounded-full flex items-center justify-center flex-shrink-0 font-semibold tracking-tight",
          size,
          theme.bgClass,
          theme.textClass,
          compact ? "text-[10px]" : "text-[11px]",
        )}
        aria-label={fullName(sender) ?? undefined}
      >
        {initials}
      </div>
    );
  }

  // 3. System icon with soft themed background
  const Icon = theme.Icon;
  return (
    <div
      className={cn(
        "rounded-xl flex items-center justify-center flex-shrink-0",
        size,
        theme.bgClass,
      )}
    >
      <Icon className={cn("h-4 w-4", theme.textClass)} aria-hidden="true" />
    </div>
  );
}

// ─── internal: photo avatar with layered fallback ────────────────────

interface PhotoAvatarProps {
  sender: SenderProfile;
  size:   string;
  theme:  NotifTheme;
}

/**
 * Renders the initials (or themed icon) first and overlays the photo
 * on top. If the `<img>` errors we flip state and keep showing the
 * fallback, so broken avatar CDNs never leave an empty circle.
 */
function PhotoAvatar({ sender, size, theme }: PhotoAvatarProps) {
  const [errored, setErrored] = useState(false);
  const initials = senderInitials(sender);
  const Icon = theme.Icon;

  return (
    <div
      className={cn(
        "relative rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border/60",
        size,
      )}
    >
      {/* Fallback layer (always rendered) */}
      <div
        className={cn(
          "absolute inset-0 flex items-center justify-center font-semibold tracking-tight text-[11px]",
          theme.bgClass,
          theme.textClass,
        )}
        aria-hidden="true"
      >
        {initials ?? <Icon className="h-4 w-4" />}
      </div>

      {/* Foreground photo — only mounted while not errored */}
      {!errored && sender.avatar_url ? (
        <img
          src={sender.avatar_url}
          alt={fullName(sender) ?? "Notification sender"}
          className="relative h-full w-full object-cover"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      ) : null}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

function fullName(p: SenderProfile | undefined | null): string | null {
  if (!p) return null;
  const joined = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return joined || p.email || null;
}

function senderInitials(p: SenderProfile | undefined | null): string | null {
  if (!p) return null;
  const first = p.first_name?.[0];
  const last  = p.last_name?.[0];
  if (first && last) return (first + last).toUpperCase();
  if (first)         return first.toUpperCase();
  const emailLead = p.email?.[0];
  return emailLead ? emailLead.toUpperCase() : null;
}
